import { useEffect, useMemo, useState } from 'react';
import { type RoleId } from '@shared';
import { apiGet, apiPatch, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { InviteUserModal, type InvitedUser } from './InviteUserModal';

interface StaffUser {
  id: string;
  email: string;
  name: string;
  role: RoleId;
  office?: string;
  status?: 'invited' | 'active' | 'suspended' | 'deactivated';
  initials?: string;
  created_at?: string;
}

interface RoleRow {
  id: RoleId;
  label: string;
  can: Array<{ resource: string; actions: Array<'read' | 'write' | 'sign'> }>;
}

const STATUS_TONE: Record<string, string> = {
  invited: 'accent',
  active: 'green',
  suspended: 'amber',
  deactivated: 'red',
};

// Curated permission columns rendered in the role-matrix table. Anything outside
// this list is rolled up under "Other". Keeps the matrix legible at-a-glance.
const PERMISSION_COLS = [
  { key: 'clients', label: 'Clients' },
  { key: 'engagements', label: 'Engagements' },
  { key: 'kyc', label: 'KYC' },
  { key: 'documents', label: 'Documents' },
  { key: 'finance', label: 'Finance' },
  { key: 'users', label: 'Users' },
  { key: 'audit', label: 'Audit' },
  { key: 'cms', label: 'CMS' },
] as const;

function actionsFor(role: RoleRow, resource: string): string {
  const row = role.can.find((c) => c.resource === resource);
  if (!row) return '—';
  const tags: string[] = [];
  if (row.actions.includes('read')) tags.push('R');
  if (row.actions.includes('write')) tags.push('W');
  if (row.actions.includes('sign')) tags.push('S');
  return tags.length ? tags.join('/') : '—';
}

export function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<StaffUser[] | null>(null);
  const [roles, setRoles] = useState<RoleRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'people' | 'roles'>('people');
  const [showInvite, setShowInvite] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  // User pending a deactivate confirmation (drives the centered confirm modal).
  const [confirmTarget, setConfirmTarget] = useState<StaffUser | null>(null);

  function handleInvited(u: InvitedUser) {
    // Prepend for instant feedback; the server list is name-sorted on next load.
    setUsers((prev) => [u as StaffUser, ...(prev ?? [])]);
  }

  // Soft delete / restore — flips the user's status via PATCH /api/users/:id.
  // The server cuts off a deactivated user's access on their next request.
  async function performStatusChange(u: StaffUser, next: 'active' | 'deactivated') {
    setError(null);
    setBusyId(u.id);
    try {
      const r = await apiPatch<{ user: StaffUser }>(`/api/users/${u.id}`, { status: next });
      setUsers((prev) => (prev ?? []).map((x) => (x.id === u.id ? { ...x, status: r.user.status } : x)));
      setConfirmTarget(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Failed to update the user. Please try again.');
    } finally {
      setBusyId(null);
    }
  }

  useEffect(() => {
    apiGet<{ users: StaffUser[] }>('/api/users')
      .then((r) => setUsers(r.users))
      .catch((err: Error) => setError(err instanceof ApiError ? err.serverMessage : err.message));
    apiGet<{ roles: RoleRow[] }>('/api/users/roles')
      .then((r) => setRoles(r.roles))
      .catch(() => { /* matrix is best-effort */ });
  }, []);

  const counts = useMemo(() => {
    let active = 0;
    let suspended = 0;
    for (const u of users ?? []) {
      if (u.status === 'active') active++;
      if (u.status === 'suspended') suspended++;
    }
    return { total: users?.length ?? 0, active, suspended };
  }, [users]);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 className="portal-h1">Users &amp; roles</h1>
          <p className="portal-sub">
            Staff roster and the role permission matrix. Read (R) lets a role view the resource,
            Write (W) lets them mutate, Sign (S) lets them apply dual sign-off on final reports.
          </p>
        </div>
        <button type="button" className="btn accent" onClick={() => setShowInvite(true)}>
          + Add user
        </button>
      </div>

      <div className="portal-cards">
        <div className="card">
          <div className="lbl">Total staff</div>
          <div className="val">{counts.total}</div>
          <div className="delta">All offices</div>
        </div>
        <div className="card">
          <div className="lbl">Active</div>
          <div className="val">{counts.active}</div>
          <div className="delta">Signing in</div>
        </div>
        <div className="card">
          <div className="lbl">Suspended</div>
          <div className="val">{counts.suspended}</div>
          <div className="delta">Temporarily disabled</div>
        </div>
        <div className="card">
          <div className="lbl">Roles defined</div>
          <div className="val">{roles?.length ?? '—'}</div>
          <div className="delta">In the permission matrix</div>
        </div>
      </div>

      <div className="tabs" style={{ marginTop: 24 }}>
        <button className={view === 'people' ? 'on' : ''} onClick={() => setView('people')}>People</button>
        <button className={view === 'roles' ? 'on' : ''} onClick={() => setView('roles')}>Role matrix</button>
      </div>

      {error && (
        <div className="card" style={{ marginTop: 16, borderColor: 'var(--red)', color: 'var(--red)' }}>
          {error}
        </div>
      )}

      {view === 'people' && (
        <div className="card flush">
          <table className="tbl">
            <thead>
              <tr>
                <th>Person</th>
                <th>Email</th>
                <th>Role</th>
                <th>Office</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users === null && (
                <tr><td colSpan={6} style={{ color: 'var(--fg-mute)', fontSize: 13 }}>Loading…</td></tr>
              )}
              {users !== null && users.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ color: 'var(--fg-mute)', fontSize: 13, textAlign: 'center', padding: 32 }}>
                    No staff users on file.
                  </td>
                </tr>
              )}
              {(users ?? []).map((u) => (
                <tr key={u.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span className="avatar">{u.initials ?? u.name.slice(0, 2).toUpperCase()}</span>
                      <div>
                        <div style={{ fontWeight: 500 }}>{u.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--fg-mute)', fontFamily: 'var(--mono)' }}>{u.id}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ color: 'var(--fg-soft)', fontSize: 13 }}>{u.email}</td>
                  <td style={{ fontSize: 13 }}>{u.role}</td>
                  <td style={{ color: 'var(--fg-soft)', fontSize: 13 }}>{u.office ?? '—'}</td>
                  <td>
                    <span className={`chip ${STATUS_TONE[u.status ?? 'active']}`}>
                      <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />
                      {u.status ?? 'active'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {u.id === me?.id ? (
                      <span style={{ fontSize: 12, color: 'var(--fg-mute)' }}>You</span>
                    ) : u.status === 'deactivated' ? (
                      <button
                        type="button"
                        className="btn ghost sm"
                        disabled={busyId === u.id}
                        onClick={() => performStatusChange(u, 'active')}
                      >
                        {busyId === u.id ? '…' : 'Reactivate'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn ghost sm"
                        style={{ color: 'var(--red)' }}
                        disabled={busyId === u.id}
                        onClick={() => setConfirmTarget(u)}
                      >
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showInvite && (
        <InviteUserModal
          roles={(roles ?? []).map((r) => ({ id: r.id, label: r.label }))}
          onClose={() => setShowInvite(false)}
          onInvited={handleInvited}
        />
      )}

      {confirmTarget && (
        <div
          className="kyc-modal-backdrop"
          onClick={() => busyId === null && setConfirmTarget(null)}
        >
          <div
            className="card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="deactivate-h"
            style={{ width: '100%', maxWidth: 440 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="deactivate-h" className="portal-h1" style={{ fontSize: 18, marginTop: 0 }}>
              Deactivate user
            </h2>
            <p style={{ color: 'var(--fg-soft)', fontSize: 14, lineHeight: 1.5 }}>
              Deactivate <strong>{confirmTarget.name}</strong>? They will lose access immediately.
              You can reactivate them later.
            </p>
            <div className="kyc-modal-actions">
              <button
                type="button"
                className="btn ghost"
                disabled={busyId !== null}
                onClick={() => setConfirmTarget(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn"
                style={{ background: 'var(--red)', borderColor: 'var(--red)', color: '#fff' }}
                disabled={busyId !== null}
                onClick={() => performStatusChange(confirmTarget, 'deactivated')}
              >
                {busyId === confirmTarget.id ? 'Deactivating…' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {view === 'roles' && (
        <div className="card flush">
          <table className="tbl">
            <thead>
              <tr>
                <th>Role</th>
                {PERMISSION_COLS.map((p) => <th key={p.key}>{p.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {roles === null && (
                <tr><td colSpan={PERMISSION_COLS.length + 1} style={{ color: 'var(--fg-mute)', fontSize: 13 }}>Loading…</td></tr>
              )}
              {(roles ?? []).map((r) => (
                <tr key={r.id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{r.label}</div>
                    <div style={{ fontSize: 12, color: 'var(--fg-mute)', fontFamily: 'var(--mono)' }}>{r.id}</div>
                  </td>
                  {PERMISSION_COLS.map((p) => {
                    const tag = actionsFor(r, p.key);
                    return (
                      <td key={p.key} style={{ fontFamily: 'var(--mono)', fontSize: 12, color: tag === '—' ? 'var(--fg-mute)' : 'var(--fg)' }}>
                        {tag}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
